"use client";

import { c as _c } from "react/compiler-runtime";
;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { useQueryClient } from "@tanstack/react-query";
import { startTransition, useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { formatRelativeTime, toTitleCase } from "@/lib/utils";
import { getMostRecentDate } from "@/lib/utils/lastSynced";
import { visualizationQueryKeys } from "@/queries/query-keys/visualization";
import type { ViewVisualization } from "@/queries/visualization/fetchers";
type QueryDataEntry = [readonly unknown[], ViewVisualization | undefined];

// Stable empty array for server snapshot
const EMPTY_ARRAY: QueryDataEntry[] = [];

// Throttle delay in ms - prevents excessive re-renders from rapid cache updates
const THROTTLE_DELAY = 100;

// Check if query key starts with the given prefix (position-aware matching)
const hasKeyPrefix = (key: readonly unknown[], prefix: readonly unknown[]): boolean => prefix.every((val, idx) => key[idx] === val);
export function LastSyncedStatus() {
  const $ = _c(34);
  const queryClient = useQueryClient();
  const {
    workspaceId,
    currentView
  } = useCurrentWorkspace();
  let t0;
  if ($[0] !== currentView) {
    t0 = currentView ? toTitleCase(currentView) : "";
    $[0] = currentView;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const viewName = t0;
  const cachedResult = useRef(EMPTY_ARRAY);
  const cachedSignature = useRef("");
  let t1;
  if ($[2] !== queryClient) {
    t1 = queryClient.getQueryCache();
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const queryCache = t1;
  let t2;
  if ($[4] !== queryClient || $[5] !== viewName || $[6] !== workspaceId) {
    t2 = () => {
      const keyPrefix = visualizationQueryKeys.view({
        viewName,
        workspaceId
      });
      const newResult = queryClient.getQueriesData({
        queryKey: keyPrefix
      });
      const matchingQueries = newResult.filter(t3 => {
        const [key] = t3;
        return hasKeyPrefix(key, keyPrefix);
      });
      const newSignature = matchingQueries.map(_temp).join(";");
      if (newSignature !== cachedSignature.current) {
        cachedSignature.current = newSignature;
        cachedResult.current = matchingQueries;
      }
      return cachedResult.current;
    };
    $[4] = queryClient;
    $[5] = viewName;
    $[6] = workspaceId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const getSnapshot = t2;
  let t3;
  if ($[8] !== queryCache || $[9] !== viewName) {
    t3 = onStoreChange => {
      let timeoutId = null;
      let pending = false;
      const unsubscribe = queryCache.subscribe(event => {
        const eventKey = event.query?.queryKey;
        if (eventKey?.[1] !== "visualization" || eventKey[2] !== viewName) {
          return;
        }
        if (timeoutId) {
          pending = true;
          return;
        }
        timeoutId = setTimeout(() => {
          startTransition(() => {
            onStoreChange();
          });
          if (pending) {
            pending = false;
            startTransition(() => {
              onStoreChange();
            });
          }
          timeoutId = null;
        }, THROTTLE_DELAY);
      });
      return () => {
        unsubscribe();
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    };
    $[8] = queryCache;
    $[9] = viewName;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const visualizationQueries = useSyncExternalStore(t3, getSnapshot, _temp2);
  let t4;
  bb0: {
    if (!visualizationQueries.length) {
      let t5;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = {
          lastSynced: null,
          visualizations: []
        };
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    let visualizations;
    if ($[12] !== visualizationQueries) {
      const vizMap = new Map();
      for (const [key_1, data_0] of visualizationQueries) {
        if (data_0 === undefined) {
          continue;
        }
        const visualizationName = key_1[3] as string;
        let lastSyncedValue = data_0?.lastSyncedAt;
        if (lastSyncedValue && typeof lastSyncedValue === "object") {
          lastSyncedValue = getMostRecentDate(lastSyncedValue as Record<string, string>);
        }
        const lastSyncedDate = lastSyncedValue ? new Date(lastSyncedValue) : null;
        const existing = vizMap.get(visualizationName);
        if (!existing || lastSyncedDate && (!existing.lastSynced || lastSyncedDate > existing.lastSynced)) {
          vizMap.set(visualizationName, {
            id: data_0?.id ?? visualizationName,
            lastSynced: lastSyncedDate,
            name: data_0?.name ?? visualizationName
          });
        }
      }
      visualizations = Array.from(vizMap.values());
      t5 = visualizations.reduce(_temp3, null as Date | null);
      $[12] = visualizationQueries;
      $[13] = t5;
      $[14] = visualizations;
    } else {
      t5 = $[13];
      visualizations = $[14];
    }
    const lastSynced = t5;
    let t6;
    if ($[15] !== lastSynced || $[16] !== visualizations) {
      t6 = {
        lastSynced,
        visualizations
      };
      $[15] = lastSynced;
      $[16] = visualizations;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t4 = t6;
  }
  const {
    lastSynced: lastSynced_0,
    visualizations: visualizations_0
  } = t4;
  if (!visualizations_0.length) {
    return null;
  }
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span>Synced:</span>;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== lastSynced_0) {
    t6 = formatRelativeTime(lastSynced_0);
    $[19] = lastSynced_0;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t6) {
    t7 = <TooltipTrigger asChild={true}><PopoverTrigger asChild={true}><button aria-label="Show last synced details" className="flex items-center gap-1 text-xs text-muted-foreground px-2 py-1 rounded hover:bg-muted transition-colors" type="button">{t5}<span className="font-medium">{t6}</span></button></PopoverTrigger></TooltipTrigger>;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <TooltipContent><p>View sync details</p></TooltipContent>;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7) {
    t9 = <Tooltip>{t7}{t8}</Tooltip>;
    $[24] = t7;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="font-semibold text-xs mb-2">Synced</div>;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== visualizations_0) {
    t11 = visualizations_0.map(_temp4);
    $[27] = visualizations_0;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11) {
    t12 = <PopoverContent align="end" className="w-80">{t10}<ul className="space-y-1">{t11}</ul></PopoverContent>;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t9) {
    t13 = <TooltipProvider><Popover>{t9}{t12}</Popover></TooltipProvider>;
    $[31] = t12;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function _temp4(viz) {
  return <li className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 text-xs" key={viz.id}><span className="min-w-0 whitespace-normal wrap-break-word leading-snug">{viz.name}</span><span className="tabular-nums text-muted-foreground font-medium text-right">{formatRelativeTime(viz.lastSynced)}</span></li>;
}
function _temp3(latest, visualization) {
  if (!visualization.lastSynced) {
    return latest;
  }
  if (!latest || visualization.lastSynced > latest) {
    return visualization.lastSynced;
  }
  return latest;
}
function _temp2() {
  return EMPTY_ARRAY;
}
function _temp(t0) {
  const [key_0, data] = t0;
  return `${key_0[3]}|${data?.lastSyncedAt ?? ""}`;
}
export default LastSyncedStatus;